import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { format } from 'date-fns';
import axios from 'axios';
import { AppointmentStatus, AppointmentType } from '@bb/domain/dist/types';
import AppointmentConfig from '@bb/domain/dist/types/AppointmentConfig';
import { difference } from 'lodash';
import findListingConfirmed from '../../../api/AppointmentController/findListingConfirmed';

type DataType = {
  key: React.Key;
  updateType: 'accessChange' | 'autoConfirm';
  time: Date;
  timeEnd: Date;
  showingAgentName: string;
  showingOrgName: string;
  showingType: AppointmentType;
  status: string;
  note: string;
  lastAction: Date;
  actionTaken: string;
};

type UpdateExistingShowingsModalProps = {
  listingId: string;
  visible: boolean;
  showingsConfig: AppointmentConfig;
  excludedShowings: AppointmentStatus[];
  hideModal: () => void;
  saveForm: () => void;
};

const columns: ColumnsType<DataType> = [
  {
    title: 'TYPE OF UPDATE',
    dataIndex: 'updateType',
    key: 'updateType',
    render: updateType => {
      switch (updateType as DataType['updateType']) {
        case 'accessChange':
          return 'Access Change';
        case 'autoConfirm':
          return 'Auto-Confirm';
        default:
          return 'Unknown';
      }
    },
  },
  {
    title: 'DATE',
    dataIndex: 'time',
    key: 'showingDate',
    render: date => format(new Date(date), 'EEE MMMM do'),
  },
  {
    title: 'TIME',
    key: 'showingTime',
    render: (_, record) => {
      const from = format(new Date(record.time), 'p');
      const to = format(new Date(record.timeEnd), 'p');
      return `${from} - ${to}`;
    },
  },
  {
    title: 'SHOWING AGENT',
    key: 'showingAgentName',
    render: (_, record) => (
      <>
        {record.showingAgentName}
        <div className="table-cell-light">
          <strong>{record.showingOrgName}</strong>
        </div>
        <span
          className={
            [
              'Buyer Visit',
              'Appraisal',
              'Home Inspection',
              'Photographer',
              'Pest Control',
            ].includes(record.showingType)
              ? 'text-danger'
              : ''
          }
          style={{ fontSize: '11px' }}
        >
          {record.showingType}
        </span>
      </>
    ),
  },
  {
    title: 'STATUS',
    dataIndex: 'note',
    key: 'note',
    width: 210,
    render: (status, record) => (
      <>
        {status} <br /> {format(record.lastAction, 'MMM do, Y p')}
      </>
    ),
  },
];

const UpdateExistingShowingsModal: React.FC<
  UpdateExistingShowingsModalProps
> = ({
  listingId,
  visible,
  showingsConfig,
  excludedShowings,
  hideModal,
  saveForm,
}) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataType[] | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const continueUpdate = () => {
    hideModal();
    setLoading(false);
    saveForm();
    setSelectedRowKeys([]);
  };

  const fetch = async () => {
    const response = await findListingConfirmed(listingId);

    const formattedDataSource: DataType[] = response
      .filter(({ appointment: { status, isPending } }) => {
        if (isPending) return false;
        return ['new', 'confirm'].includes(status);
      })
      .sort((a, b) => {
        const dateA = new Date(a.appointment.time);
        const dateB = new Date(b.appointment.time);
        if (dateA < dateB) return 1;
        if (dateA > dateB) return -1;
        return 0;
      })
      .map(({ appointment: showing }) => ({
        key: showing.id,
        updateType: showing.status === 'new' ? 'autoConfirm' : 'accessChange',
        time: new Date(showing.time),
        timeEnd: new Date(showing.timeEnd),
        showingAgentName: showing.coop.name,
        showingOrgName: showing.coop.org?.name,
        showingType: showing.type,
        status: showing.status,
        note: showing.legacyStatus.note,
        lastAction: new Date(showing.lastActionAt),
        actionTaken: showing.lastAction.actionTaken,
      }));

    setDataSource(formattedDataSource);
  };

  const handleUpdate = async () => {
    setLoading(true);

    const selectedAppointments = selectedRowKeys.map(key =>
      dataSource.find(data => data.key === key),
    );

    const response = await axios.post(
      `/appointment/update_by_showings_config`,
      {
        showings: selectedAppointments.map(showing => ({
          id: showing.key,
          status: showing.status,
        })),
        config: {
          ...showingsConfig,
        },
      },
    );

    if (response.status !== 200) {
      message.error('Something wrong happened. Try again later');
      setLoading(false);
      return;
    }

    continueUpdate();
  };

  useEffect(() => {
    fetch();
  }, []);

  const filteredDataSource = useMemo(() => {
    const val = (dataSource ?? []).filter(
      data =>
        new Date(data.time) > new Date() &&
        difference(['new', 'confirm'], excludedShowings).includes(data.status),
    );
    if (visible && !val.length) {
      continueUpdate();
    } else {
      setSelectedRowKeys(val.map(({ key }) => key));
    }
    return val;
  }, [excludedShowings]);

  return (
    <Modal
      data-cy="modal-popup"
      title="Update Existing Showings"
      confirmLoading
      centered
      open={visible && filteredDataSource?.length > 0}
      onOk={handleUpdate}
      onCancel={hideModal}
      footer={[
        <Button key="back" onClick={continueUpdate}>
          Do Not Update
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          disabled={!selectedRowKeys.length || loading}
          onClick={handleUpdate}
        >
          Update{selectedRowKeys.length ? ` (${selectedRowKeys.length}) ` : ' '}
          Showings
        </Button>,
      ]}
      width="max(500px, 60vw)"
      bodyStyle={{
        minHeight: '50vh',
        minWidth: '50vw',
        overflowY: 'auto',
        maxHeight: '70vh',
      }}
    >
      <Table
        pagination={false}
        dataSource={filteredDataSource}
        columns={columns}
        loading={!dataSource}
        rowSelection={{
          type: 'checkbox',
          onChange: (keys: React.Key[]) => {
            setSelectedRowKeys(keys);
          },
          selectedRowKeys,
        }}
      />
    </Modal>
  );
};

export default UpdateExistingShowingsModal;
